import { useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import {
    useGetCuitPresentacionesSvccQuery,
    useConsolidateCarcinogenicSRTMutation,
    useSyncCarcinogenicSRTNewsMutation,
    useLazyGetCarcinogenicPendingByCuitQuery,
    useVincularCarcinogenicSRTByCuitMutation,
    useLazyVerificarSincronizacionSrtQuery,
    useGetValidacionesCuitsSvccQuery,
    useCancelCarcinogenicSRTByContractMutation,
    useModifyCarcinogenicSRTMutation,
    useNotificarClienteSvccMutation
} from '../../../services/carcinogenicApi';
import { handleErrors } from '../../../utils/common';
import { ErrorSvcc } from '../../Presentaciones/components/HallazgosEstablecimiento';

export type EstadoCuitSvcc =
    'SIN_INICIAR' | 'RECHAZADA' | 'PRESENTADA_SIN_CONSOLIDAR' | 'CONSOLIDADA';

export interface BloqueadoVincular {
    idInforme: number;
    errores: ErrorSvcc[];
}

export interface ResultadoVerificacionSrt {
    desincronizada: boolean;
    srtConsolidado: boolean;
    idPresentacionSrt: number | null;
}

/**
 * Resumen de la validación pre-envío del lote de un CUIT, tal como lo devuelve
 * `POST /srt/cuit/validaciones`. `error: true` dice que ese CUIT no se pudo
 * verificar, que no es lo mismo que verificado y limpio: en ese caso el envío
 * queda habilitado, porque la red de seguridad real es el 400 de vincular.
 */
export interface ValidacionInformeSvcc {
    completo: boolean;
    errores: ErrorSvcc[];
    /**
     * Si lo que se enviaría hoy difiere de lo último declarado ante la SRT.
     * `SIN_REFERENCIA` es "no se puede saber" —nunca se envió, o se envió antes
     * de que se guardara la huella—, y no hay que leerlo como un "no".
     */
    novedadesSinInformar?: 'SI' | 'NO' | 'SIN_REFERENCIA';
}

export type ResumenValidacionCuit =
    | {
          bloqueantes: number;
          advertencias: number;
          informes: number;
          /**
           * Informes del CUIT cuya carga se apartó de lo declarado ante la SRT.
           * Cuenta sólo lo comprobable: un informe sin huella previa no suma,
           * porque no hay contra qué compararlo.
           */
          informesConNovedades?: number;
          /**
           * Detalle por informe, el mismo que muestra el drawer. Viene en esta
           * respuesta porque el backend ya lo calcula para contar bloqueantes:
           * así el drawer no tiene que volver a pedir lo mismo.
           */
          porInforme?: Record<number, ValidacionInformeSvcc>;
      }
    | { error: true };

export const tieneBloqueantes = (resumen?: ResumenValidacionCuit): boolean =>
    !!resumen && !('error' in resumen) && resumen.bloqueantes > 0;

/**
 * Cuántos informes del CUIT tienen novedades sin informar. Cero también cuando
 * el CUIT no se pudo verificar: sin validación no hay comparación, y suponer
 * que hay novedades sería tan falso como suponer que no.
 */
export const informesConNovedades = (resumen?: ResumenValidacionCuit): number =>
    !resumen || 'error' in resumen ? 0 : (resumen.informesConNovedades ?? 0);

export type OrigenPeriodoSvcc = 'DECLARADO' | 'INFERIDO' | 'SIN_PERIODO';

export interface CuitPresentacionSvcc {
    cuit: string;
    /**
     * El período del que habla la fila. Hay una fila por CUIT × período con
     * informes; el envío es de un período por vez, así que sólo una de ellas
     * —la activa— acciona y las demás esperan (ver `periodoActivo`). `null` es
     * "sin período": nadie lo declaró y el informe todavía no se envió.
     */
    anioPeriodo: number | null;
    /**
     * Si el año lo declaró alguien o lo dedujo el backend. Un período inferido
     * no se muestra igual que uno declarado: puede no coincidir con lo que la
     * SRT registró.
     */
    origenPeriodo: OrigenPeriodoSvcc;
    /**
     * Si esta fila es la que acciona el CUIT: el período más viejo con
     * informes sin enviar (o el último, si ya se envió todo). Abajo la SRT
     * admite un solo trámite por CUIT, así que los demás períodos esperan a
     * que éste cierre. El estado, los conteos y el filtro por estado son los
     * de la fila activa. Con un backend anterior no viene: toda fila es activa.
     */
    periodoActivo?: boolean;
    /** El año de la fila activa del CUIT: lo que una fila en espera está esperando. */
    periodoEnCurso?: number | null;
    /** Los informes de esta fila (CUIT × período). */
    idsInformes?: number[];
    /**
     * Cuántos de esos informes siguen sin enviar. Ojo con lo que NO cuenta: un
     * trabajador agregado a un informe ya presentado no deja rastro en la base,
     * así que no aparece acá. Por eso la pantalla habla de "informes sin
     * enviar" y no de "novedades pendientes", que prometería una detección que
     * no existe.
     */
    informesSinEnviar?: number;
    /**
     * Informes que ya pasaron por la SRT y volvieron a BORRADOR: el cliente los
     * está corrigiendo. No suman a `informesSinEnviar` (no están listos para
     * enviar), pero mantienen al CUIT en la grilla. Lo trae el backend desde
     * svcc-grilla-borradores-enviados; con uno anterior no viene.
     */
    informesEnBorrador?: number;
    /**
     * Cobertura de RAR del lote: cuántos de los `cantidadEstablecimientos`
     * tienen al menos un RAR presentado. Es lo que cuenta el ícono de RAR de
     * la celda de período; contar PDFs mezclaba períodos y establecimientos en
     * un número que no decía si algún establecimiento quedaba sin RAR.
     */
    establecimientosConRar?: number;
    nombreOrganizacion: string | null;
    /** Cuántos informes entran en el lote de esta fila. */
    cantidadInformes: number;
    /**
     * Si el contrato del CUIT sigue vigente. Con el contrato dado de baja la
     * fila se muestra deshabilitada y sin acciones: no hay nada que presentar
     * en nombre de un contrato terminado.
     */
    contratoActivo: boolean;
    /**
     * Lo que la SRT va a recibir como nómina contra lo que el informe declara.
     *
     * - `trabajadoresParaEnviar`: la nómina VIVA del establecimiento, que es lo
     *   que el Anexo III manda. No sale de ningún RAR.
     * - `expuestosDeclarados`: los que el informe marcó como expuestos.
     * - `expuestosFueraDeNomina`: expuestos declarados cuya fila viva ya no
     *   pertenece al establecimiento. Se declaran y NO viajan. Con esto > 0, o
     *   con cero trabajadores, el envío no dice lo que el informe cree decir.
     */
    nomina: {
        trabajadoresParaEnviar: number;
        /**
         * Nómina cargada del organismo SIN establecimiento asignado. Con cero
         * trabajadores para enviar distingue "no hay nómina" de "hay nómina
         * sin asignar": la acción es asignar, no importar.
         */
        trabajadoresSinEstablecimiento: number;
        expuestosDeclarados: number;
        expuestosFueraDeNomina: number;
        /**
         * Subconjunto de `expuestosFueraDeNomina`: fila viva sin NINGÚN
         * establecimiento. No es una reasignación: falta asignarlo.
         */
        expuestosSinEstablecimiento: number;
        /**
         * Subconjunto de `expuestosFueraDeNomina`: los que pertenecen a OTRO
         * CUIT. Con nombre: el informe declara a un trabajador de otra empresa.
         */
        expuestosDeOtroCuit: Array<{
            cuil: string;
            apellido: string;
            nombre: string;
            cuit: string | null;
            empresa: string | null;
        }>;
    };
    /**
     * Los RAR presentados de los establecimientos de la fila, del más nuevo al
     * más viejo. Son para abrirlos y mirar; la grilla ya no elige uno como "el
     * origen" porque el modelo no guarda ese vínculo.
     */
    rarsDelEstablecimiento: Array<{
        id: number;
        fechaPresentacion: string | null;
        establecimiento: string | null;
        /** Trabajadores del snapshot que el RAR congeló al presentarse. */
        trabajadores: number;
        /**
         * Año calendario de la presentación (hora argentina). Con él se sabe
         * qué RAR describe la nómina del período de la fila: los de ese año, o
         * el vigente al cierre (el más reciente hasta el 31/12). Lo calcula el
         * backend; con uno anterior a este cambio no viene.
         */
        anio?: number | null;
    }>;
    cantidadEstablecimientos: number;
    /**
     * Establecimientos con informe cargado que NO entraron en la presentación.
     * Sólo puede ser > 0 con el CUIT consolidado: consolidar es irreversible, así
     * que esos informes ya no pueden sumarse al trámite cerrado.
     */
    establecimientosSinPresentar: number;
    estado: EstadoCuitSvcc;
    idPresentacionSrt: number | null;
    fechaUltimoIntento: string | null;
    /**
     * Fecha estimada en que la SRT purgaría el borrador sin consolidar, o null
     * si no hay trámite abierto que purgar. La calcula el backend; los días
     * restantes los deriva `VigenciaBadge` contra el reloj del browser.
     */
    fechaVencimientoEstimada: string | null;
    motivoRechazo: string | null;
}

type BadgeSeleccionado = 'TODAS' | EstadoCuitSvcc;

const ESTADOS: EstadoCuitSvcc[] = [
    'SIN_INICIAR',
    'RECHAZADA',
    'PRESENTADA_SIN_CONSOLIDAR',
    'CONSOLIDADA'
];

const usePresentacionesSVCC = (cuitContrato?: string, idOrganizacion?: number) => {
    const [badgeSeleccionado, setBadgeSeleccionado] = useState<BadgeSeleccionado>('TODAS');

    /**
     * Paginación y filtros: los resuelve el backend. `GET
     * /srt/cuit/presentaciones` acepta `skip`/`take`, `estado` y `cuit`, y
     * devuelve `{ data, count, conteos }`. Filtrar en el cliente dejaría fuera
     * los CUIT de las páginas que no se pidieron, y contar los badges sobre la
     * página daría números que cambian al pasar de página.
     */
    const [paginacion, setPaginacion] = useState({ skip: 0, take: 10 });

    /**
     * Qué período se está mirando. Arranca en TODOS porque la grilla también
     * sirve para revisar lo ya presentado; lo que cambia es que ahora se puede
     * elegir y cada fila dice de qué año habla. Antes venían todos los años
     * mezclados sin manera de distinguirlos.
     */
    const [periodoSeleccionado, setPeriodoSeleccionado] = useState<string>('TODOS');

    const { data, isLoading, isFetching, refetch } = useGetCuitPresentacionesSvccQuery({
        idOrganizacion,
        cuit: cuitContrato,
        estado: badgeSeleccionado === 'TODAS' ? undefined : badgeSeleccionado,
        anioPeriodo: periodoSeleccionado === 'TODOS' ? undefined : periodoSeleccionado,
        skip: paginacion.skip,
        take: paginacion.take
    });
    const [dialogConsolidarCuit, setDialogConsolidarCuit] = useState<string | null>(null);
    const [vinculandoCuit, setVinculandoCuit] = useState<string | null>(null);
    const [bloqueadosVincular, setBloqueadosVincular] = useState<BloqueadoVincular[] | null>(null);
    // Nombre de establecimiento por informe, capturado del preview de
    // `pendientes` que ya se pide antes de vincular. Sin esto, el modal de
    // bloqueados sólo puede mostrar el id del informe y no se sabe a qué
    // establecimiento corresponde cada bloque de hallazgos.
    const [nombresPorInforme, setNombresPorInforme] = useState<Record<number, string>>({});
    const [verificandoCuit, setVerificandoCuit] = useState<string | null>(null);
    const [resultadosVerificacion, setResultadosVerificacion] = useState<
        Record<string, ResultadoVerificacionSrt>
    >({});
    const [cancelandoCuit, setCancelandoCuit] = useState<string | null>(null);
    const [modificandoCuit, setModificandoCuit] = useState<string | null>(null);
    const [notificandoClienteCuit, setNotificandoClienteCuit] = useState<string | null>(null);

    const [consolidar, { isLoading: isConsolidando }] = useConsolidateCarcinogenicSRTMutation();
    const [getPendingByCuit] = useLazyGetCarcinogenicPendingByCuitQuery();
    const [vincularSRT] = useVincularCarcinogenicSRTByCuitMutation();
    const [verificarSincronizacionQuery] = useLazyVerificarSincronizacionSrtQuery();
    const [cancelarSrtMutation] = useCancelCarcinogenicSRTByContractMutation();
    const [modificarSrtMutation] = useModifyCarcinogenicSRTMutation();
    const [notificarClienteMutation] = useNotificarClienteSvccMutation();
    const [sincronizarNovedades, { isLoading: isSincronizandoNovedades }] =
        useSyncCarcinogenicSRTNewsMutation();

    /**
     * La página que devolvió el backend. Ya viene filtrada por badge y por
     * contrato: acá no se filtra nada.
     *
     * Tolera las dos formas de la respuesta a propósito: la nueva
     * (`{ data: { data, count, conteos } }`) y la vieja (`{ data: [...] }`, sin
     * paginar). Un front nuevo contra un backend viejo —o al revés, que es lo
     * que pasó al desplegar el backend antes que el front— tiraba
     * `cuits.filter is not a function` en render y dejaba la pantalla en blanco.
     * Un desfasaje de deploy tiene que degradar a una grilla sin paginar, no a
     * una pantalla muerta.
     */
    const respuesta = data?.data;
    const esRespuestaSinPaginar = Array.isArray(respuesta);
    const cuitsPagina: CuitPresentacionSvcc[] = esRespuestaSinPaginar
        ? respuesta
        : Array.isArray(respuesta?.data)
          ? respuesta.data
          : [];
    const totalCuitsFiltrados: number = esRespuestaSinPaginar
        ? respuesta.length
        : (respuesta?.count ?? 0);

    /*
     * Cambiar de badge o de contrato cambia el conjunto: quedarse en la página
     * 4 de un filtro que ahora tiene 2 filas deja la tabla vacía sin
     * explicación. Se resetea antes de que salga el pedido, porque el skip
     * viejo viaja en la query.
     */
    useEffect(() => {
        setPaginacion((previo) => (previo.skip === 0 ? previo : { ...previo, skip: 0 }));
    }, [badgeSeleccionado, cuitContrato, periodoSeleccionado]);

    /*
     * La misma corrección para cuando el conjunto se achica por otra vía (se
     * refresca la grilla y hay menos CUIT que antes): sin esto queda pidiendo
     * una página que ya no existe y la tabla se ve vacía.
     */
    useEffect(() => {
        setPaginacion((previo) =>
            previo.skip > 0 && previo.skip >= totalCuitsFiltrados && !isFetching
                ? { ...previo, skip: 0 }
                : previo
        );
    }, [totalCuitsFiltrados, isFetching]);

    /**
     * Valida los lotes de los CUITs que la grilla está mostrando, para que la
     * fila pueda deshabilitar el envío sin que el operador tenga que abrir el
     * drawer de cada una.
     *
     * Va aparte de la grilla y no dentro de ella: validar es la operación más
     * cara del módulo, así que la tabla se dibuja primero y los botones
     * resuelven después. Sólo se piden los CUITs con envío posible — un
     * consolidado no tiene nada que validar — y el backend topea en 50.
     */
    const cuitsAValidar = useMemo(
        () =>
            /*
             * El `Set` no es cosmético: la grilla agrupa por CUIT × PERÍODO, así
             * que un CUIT con dos años abiertos ocupa dos filas y mandaba su
             * CUIT repetido. El DTO del endpoint es `@ArrayUnique`, y ese 400
             * dejaba sin validar la página ENTERA, no sólo la fila repetida.
             * Validar una vez por CUIT es además lo correcto: el lote que se
             * valida es el del CUIT, no el del período.
             */
            Array.from(
                // `Array.from` y no spread del Set: el target de este tsconfig
                // es menor a ES2015 y el spread no compila (TS2802).
                new Set(
                    cuitsPagina
                        .filter(
                            (c) =>
                                // Las filas en espera comparten CUIT con la
                                // activa y no tienen trámite: no suman nada.
                                c.periodoActivo !== false &&
                                (c.estado === 'SIN_INICIAR' || c.estado === 'RECHAZADA')
                        )
                        .map((c) => c.cuit)
                )
            )
                /*
                 * `POST /srt/cuit/validaciones` rechaza con 400 el lote de más
                 * de MAX_CUITS_POR_REQUEST (50) — y ese 400 se traga acá abajo,
                 * así que pasarse dejaba la página ENTERA sin validar. Con
                 * páginas de 10/25/50 no se llega; con la opción de 100 sí.
                 * El tope va DESPUÉS del dedupe: son 50 CUITs distintos.
                 */
                .slice(0, 50),
        [cuitsPagina]
    );

    /**
     * La validación es una query cacheada de RTK Query, no una mutation con
     * estado propio: la clave de caché ignora el orden de los CUIT, así que
     * volver a una página ya validada no dispara nada, y cualquier mutation que
     * pueda cambiar el veredicto (vincular, consolidar, cancelar, modificar, o
     * una edición del informe) invalida el tag y la próxima lectura refetchea.
     *
     * `refetchOnMountOrArgChange: 60` cubre lo que cambió por fuera —otro
     * usuario, otra pestaña— sin repetir el pedido mientras el operador trabaja.
     */
    const {
        data: respuestaValidaciones,
        isFetching: validandoCuits,
        isError: validacionesFallaron
    } = useGetValidacionesCuitsSvccQuery(cuitsAValidar, {
        skip: cuitsAValidar.length === 0,
        refetchOnMountOrArgChange: 60
    });

    /*
     * Un error se traga a propósito: una validación caída no puede bloquear la
     * operación ni ensuciar la pantalla con un cartel. Los CUITs quedan sin
     * resumen y la fila los trata como no verificados, con el envío habilitado.
     */
    const validacionesPorCuit: Record<string, ResumenValidacionCuit> = useMemo(
        () => (validacionesFallaron ? {} : (respuestaValidaciones?.data ?? {})),
        [respuestaValidaciones, validacionesFallaron]
    );

    /**
     * Detalle por informe de cada CUIT de la página, para que el drawer lo use
     * en vez de volver a pedirlo. Se arma sólo con lo que el backend mandó: un
     * CUIT sin `porInforme` (respuesta de un backend viejo, o un CUIT que no se
     * pudo verificar) simplemente no está, y el drawer valida por su cuenta.
     */
    const detalleValidacionPorCuit = useMemo(() => {
        const porCuit: Record<string, Record<number, ValidacionInformeSvcc>> = {};
        for (const [cuit, resumen] of Object.entries(validacionesPorCuit)) {
            if (resumen && !('error' in resumen) && resumen.porInforme) {
                porCuit[cuit] = resumen.porInforme;
            }
        }
        return porCuit;
    }, [validacionesPorCuit]);

    /**
     * Los conteos de los badges los cuenta el backend sobre todo el alcance del
     * usuario: con la grilla paginada el cliente sólo tiene una página, así que
     * contarlos acá daría números que cambian al pasar de página y que se irían
     * a cero al elegir un badge.
     */
    const conteos = useMemo(() => {
        const base: Record<BadgeSeleccionado, number> = {
            TODAS: 0,
            SIN_INICIAR: 0,
            RECHAZADA: 0,
            PRESENTADA_SIN_CONSOLIDAR: 0,
            CONSOLIDADA: 0
        };

        const delBackend = esRespuestaSinPaginar ? null : respuesta?.conteos;

        /*
         * Sin conteos del backend (respuesta vieja, sin paginar) se cuentan acá:
         * en ese caso la respuesta trae TODOS los CUIT, así que el número es el
         * mismo. Con la grilla paginada esto no serviría — el cliente sólo tiene
         * una página — y por eso los cuenta el backend.
         */
        if (!delBackend) {
            // Los badges cuentan CUIT, y el estado del CUIT lo dice su fila activa.
            const activas = cuitsPagina.filter((c) => c.periodoActivo !== false);
            base.TODAS = activas.length;
            for (const estado of ESTADOS) {
                base[estado] = activas.filter((c) => c.estado === estado).length;
            }
            return base;
        }

        base.TODAS = delBackend.TODAS ?? 0;
        for (const estado of ESTADOS) {
            base[estado] = delBackend[estado] ?? 0;
        }
        return base;
        // `cuitsPagina` sale de `data`, así que no agrega dependencia real.
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const cerrarBloqueadosVincular = () => setBloqueadosVincular(null);

    // El resultado de una verificación anterior deja de ser confiable en
    // cuanto el usuario dispara una acción que puede cambiar el estado real
    // (vincular/consolidar) — se descarta al empezar, no al terminar, porque
    // ya desde el click el veredicto viejo está potencialmente obsoleto.
    const descartarResultadoVerificacion = (cuit: string) => {
        setResultadosVerificacion((prev) => {
            if (!(cuit in prev)) return prev;
            const { [cuit]: _omitido, ...resto } = prev;
            return resto;
        });
    };

    const vincularCuit = async (cuit: string) => {
        setVinculandoCuit(cuit);
        setBloqueadosVincular(null);
        descartarResultadoVerificacion(cuit);
        try {
            const result = await getPendingByCuit(cuit).unwrap();
            const data = result?.data?.data ?? result?.data ?? result ?? [];
            const pendientes = Array.isArray(data) ? data : [];
            const idInformes = pendientes.map((e: any) => e.idInforme);

            setNombresPorInforme(
                Object.fromEntries(
                    pendientes
                        .filter((e: any) => e.idInforme && e.nombreEstablecimiento)
                        .map((e: any) => [e.idInforme, e.nombreEstablecimiento])
                )
            );

            if (idInformes.length === 0) {
                enqueueSnackbar('No se encontraron establecimientos pendientes para este CUIT.', {
                    variant: 'info'
                });
                return;
            }

            await vincularSRT({ cuit, idInformes }).unwrap();
            // Mensaje deliberadamente neutro: el backend puede devolver 200 sin
            // haber enviado nada nuevo (idempotencia — CUIT ya vinculado con el
            // mismo lote en paso >= 4). No podemos distinguir ese caso de un envío
            // real desde la respuesta, así que no afirmamos éxito.
            enqueueSnackbar('Solicitud procesada. Revisá el estado en la grilla.', {
                variant: 'info'
            });
        } catch (error: any) {
            const detalle = error?.data?.bloqueados;
            if (Array.isArray(detalle) && detalle.length) {
                setBloqueadosVincular(detalle);
                enqueueSnackbar(
                    'No se envió nada. Corregí los establecimientos señalados y volvé a intentar.',
                    { variant: 'error' }
                );
                return;
            }

            /**
             * La mutación no invalida tags cuando falla (a propósito: remontaría
             * el drawer de vinculación de Presentaciones y perdería su estado),
             * así que sin este refetch la grilla sigue mostrando el estado
             * anterior — el operador recibe el aviso pero la fila no se mueve.
             * Acá sí hubo envío: el estado en la SRT y el local ya cambiaron.
             */
            refetch();

            /**
             * El envío falló a mitad de camino y el backend intentó cancelar el
             * trámite que quedó abierto en la SRT. Sin decirlo, el operador no
             * sabe si puede reenviar (CUIT libre) o si el CUIT quedó bloqueado
             * y hay que liberarlo a mano — que es la diferencia entre seguir
             * trabajando y abrir un ticket a la SRT.
             */
            const cancelacion = error?.data?.cancelacionAutomatica;
            if (cancelacion?.intentada) {
                const motivo = error?.data?.message ?? 'El envío no se completó.';
                if (cancelacion.exitosa) {
                    enqueueSnackbar(
                        `${motivo} El trámite abierto se canceló en la SRT: el CUIT quedó libre para corregir y enviar de nuevo.`,
                        { variant: 'warning', autoHideDuration: 12000 }
                    );
                } else {
                    enqueueSnackbar(
                        `${motivo} ADEMÁS no se pudo cancelar el trámite: el CUIT sigue bloqueado en la SRT y hay que cancelarlo manualmente.`,
                        { variant: 'error', persist: true }
                    );
                }
                return;
            }

            handleErrors(error);
        } finally {
            setVinculandoCuit(null);
        }
    };

    const verificarSincronizacion = async (cuit: string) => {
        setVerificandoCuit(cuit);
        try {
            const result = await verificarSincronizacionQuery(cuit).unwrap();
            const data = result?.data ?? result;
            setResultadosVerificacion((prev) => ({ ...prev, [cuit]: data }));
            if (data?.desincronizada === false) {
                enqueueSnackbar('Sincronizado, sin cambios.', { variant: 'info' });
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setVerificandoCuit(null);
        }
    };

    /**
     * Cancela en la SRT la presentación abierta del CUIT para liberarlo y poder
     * volver a iniciar desde cero. Es la única salida cuando la presentación
     * quedó frenada a mitad de camino: la SRT no admite reenviar sobre una
     * presentación abierta.
     */
    const cancelarPresentacion = async (cuit: string) => {
        setCancelandoCuit(cuit);
        descartarResultadoVerificacion(cuit);
        try {
            await cancelarSrtMutation(cuit).unwrap();
            enqueueSnackbar('Presentación cancelada en la SRT.', { variant: 'success' });
        } catch (error) {
            handleErrors(error);
        } finally {
            setCancelandoCuit(null);
        }
    };

    /**
     * Reenvía a la SRT los datos corregidos de una presentación YA consolidada
     * (ModificacionDeAnexoI/V/Nómina). No cambia el estado local: la
     * presentación sigue consolidada, el intento queda en el historial.
     */
    const modificarPresentacion = async (cuit: string) => {
        setModificandoCuit(cuit);
        try {
            await modificarSrtMutation(cuit).unwrap();
            enqueueSnackbar('Modificación enviada a la SRT.', { variant: 'success' });
        } catch (error) {
            handleErrors(error);
        } finally {
            setModificandoCuit(null);
        }
    };

    /**
     * Le avisa al cliente que la SRT rechazó su presentación, para que corrija
     * lo observado. Encola el evento `SVCC_RECHAZADA_NOTIFICAR_CLIENTE`.
     *
     * El botón se había sacado de la grilla el 26/08 esperando automatizar el
     * aviso. Eso no pasó, y mientras tanto nada le avisaba al cliente: un lote
     * rechazado se quedaba esperando una corrección que nadie le había pedido.
     */
    const notificarCliente = async (cuit: string) => {
        setNotificandoClienteCuit(cuit);
        try {
            await notificarClienteMutation(cuit).unwrap();
            enqueueSnackbar('Cliente notificado correctamente.', { variant: 'success' });
        } catch (error) {
            handleErrors(error);
        } finally {
            setNotificandoClienteCuit(null);
        }
    };

    /**
     * Consolidar no cierra el circuito: el empleador tiene 10 días corridos para
     * confirmar o rechazar, y hasta que eso pase no se puede modificar la
     * presentación ni iniciar otra para el CUIT (Anexo IV §1.4,
     * `InformacionAdicionalART_V2.1.pdf` §3.2.2 y §3.7.2).
     *
     * El estado lo informa el WS de Novedades y nadie lo consultaba: el endpoint
     * existía sin nada que lo llamara, así que toda presentación consolidada
     * quedaba en "sin novedad" para siempre y el CUIT trabado.
     */
    const handleSincronizarNovedades = async (cuit: string) => {
        try {
            const respuesta = await sincronizarNovedades(cuit).unwrap();
            const estado = respuesta?.data?.actualizados?.[0]?.estado;

            enqueueSnackbar(
                estado
                    ? `Novedades sincronizadas. Estado: ${estado}`
                    : 'Novedades sincronizadas: la SRT todavía no informó nada para este CUIT',
                { variant: estado ? 'success' : 'info' }
            );
        } catch (error) {
            handleErrors(error);
        }
    };

    const abrirConsolidar = (cuit: string) => setDialogConsolidarCuit(cuit);
    const cerrarConsolidar = () => setDialogConsolidarCuit(null);

    const handleConsolidar = async (cuit: string) => {
        descartarResultadoVerificacion(cuit);
        try {
            await consolidar(cuit).unwrap();
            enqueueSnackbar('Consolidación SRT realizada con éxito', { variant: 'success' });
            cerrarConsolidar();
        } catch (error) {
            // Antes mostraba un "Algo salió mal..." genérico que tapaba el
            // motivo real de la SRT ("CUIT ya consolidado", "no hay
            // presentación abierta"), que es justo lo que el operador necesita
            // para saber si tiene que hacer algo o no.
            handleErrors(error);
        }
    };

    return {
        // `cuits` es la página vigente: el drawer y los diálogos re-derivan su
        // fila de acá, y sólo se abren desde una fila que está en pantalla.
        cuits: cuitsPagina,
        cuitsPagina,
        totalCuitsFiltrados,
        paginacion,
        setPaginacion,
        isLoading: isLoading || isFetching,
        badgeSeleccionado,
        setBadgeSeleccionado,
        periodoSeleccionado,
        setPeriodoSeleccionado,
        conteos,
        vincularCuit,
        vinculandoCuit,
        bloqueadosVincular,
        nombresPorInforme,
        cerrarBloqueadosVincular,
        verificandoCuit,
        resultadosVerificacion,
        verificarSincronizacion,
        validacionesPorCuit,
        detalleValidacionPorCuit,
        validandoCuits,
        cancelandoCuit,
        cancelarPresentacion,
        modificandoCuit,
        notificandoClienteCuit,
        notificarCliente,
        modificarPresentacion,
        dialogConsolidarCuit,
        abrirConsolidar,
        cerrarConsolidar,
        handleConsolidar,
        isConsolidando,
        handleSincronizarNovedades,
        isSincronizandoNovedades
    };
};

export default usePresentacionesSVCC;
